import { z } from 'zod';
import { UuidSchema } from './common.js';

// -----------------------------------------------------------------------------
// Enums
// -----------------------------------------------------------------------------

export const CampaignStatusSchema = z.enum([
  'queued',
  'parsing_goal',
  'decomposing',
  'awaiting_approval',
  'executing',
  'evaluating',
  'replanning',
  'completed',
  'failed',
  'cancelled',
]);
export type CampaignStatus = z.infer<typeof CampaignStatusSchema>;

export const CampaignStepTypeSchema = z.enum([
  'a1_audit',
  'a2_keyword',
  'a3_brief',
  'a4_report',
  'a5_competitor',
  'a6_calendar',
  // Auto-campaign additions (Phase 1 — dispatch stubs, LLM strategy prompt not yet updated).
  'precheck',
  'a7_rank',
  'a10_off_page',
  'a3_brief_batch',
  'a10_off_page_batch',
  // SERP teardown for one keyword (competitorContentAnalysisWorkflow) — the
  // brief batch inserts one per winner so each brief gets a real top-10
  // analysis to pin against, matching the manual "Who ranks for this keyword"
  // flow.
  'a5_analysis',
]);
export type CampaignStepType = z.infer<typeof CampaignStepTypeSchema>;

export const CampaignStepStatusSchema = z.enum([
  'pending',
  'queued',
  'running',
  'completed',
  'failed',
  'skipped',
  'cancelled',
]);
export type CampaignStepStatus = z.infer<typeof CampaignStepStatusSchema>;

/** Coarse strategy taxonomy the LLM picks from. */
export const StrategyArchetypeSchema = z.enum([
  'cluster_expansion',
  'technical_recovery',
  'competitor_attack',
  'mixed',
]);
export type StrategyArchetype = z.infer<typeof StrategyArchetypeSchema>;

// -----------------------------------------------------------------------------
// Rows
// -----------------------------------------------------------------------------

export const CampaignSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  status: CampaignStatusSchema,
  workflow_id: z.string().nullable(),
  goal: z.string(),
  horizon_days: z.number().int().positive().nullable(),
  constraints: z.string().nullable(),
  parsed_goal: z.unknown().nullable(),
  strategy: z.unknown().nullable(),
  briefing_md: z.string().nullable(),
  plan_history: z.array(z.unknown()).default([]),
  warnings: z.array(z.unknown()).default([]),
  started_at: z.string().datetime({ offset: true }).nullable(),
  approved_at: z.string().datetime({ offset: true }).nullable(),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  failed_at: z.string().datetime({ offset: true }).nullable(),
  failure_reason: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const CampaignStepSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  campaign_id: UuidSchema,
  seq: z.number().int(),
  step_type: CampaignStepTypeSchema,
  status: CampaignStepStatusSchema,
  title: z.string(),
  rationale: z.string().nullable(),
  payload: z.record(z.unknown()).default({}),
  depends_on: z.array(UuidSchema).default([]),
  child_workflow_id: z.string().nullable(),
  child_run_id: UuidSchema.nullable(),
  result: z.unknown().nullable(),
  started_at: z.string().datetime({ offset: true }).nullable(),
  completed_at: z.string().datetime({ offset: true }).nullable(),
  failure_reason: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
});
export type CampaignStep = z.infer<typeof CampaignStepSchema>;

export const CampaignEventSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  campaign_id: UuidSchema,
  kind: z.string(),
  detail: z.string().nullable(),
  payload: z.record(z.unknown()).default({}),
  created_at: z.string().datetime({ offset: true }),
});
export type CampaignEvent = z.infer<typeof CampaignEventSchema>;

// -----------------------------------------------------------------------------
// Requests
// -----------------------------------------------------------------------------

export const CreateCampaignRequestSchema = z.object({
  project_id: UuidSchema,
  goal: z.string().trim().min(10, 'Describe the goal in at least 10 characters').max(1000),
  horizon_days: z.number().int().min(7).max(365).optional(),
  constraints: z.string().trim().max(2000).optional(),
  /** When true, skip the HITL approval gate and auto-execute the plan. */
  auto_approve: z.boolean().optional(),
});
export type CreateCampaignRequest = z.infer<typeof CreateCampaignRequestSchema>;

export const ApproveCampaignRequestSchema = z.object({
  edits: z
    .object({
      step_id: UuidSchema,
      status: z.enum(['skipped']).optional(),
      title: z.string().max(200).optional(),
    })
    .array()
    .max(50)
    .optional(),
});
export type ApproveCampaignRequest = z.infer<typeof ApproveCampaignRequestSchema>;

// -----------------------------------------------------------------------------
// LLM outputs (validated with completeWithSchema)
// -----------------------------------------------------------------------------

export const GoalParseOutputSchema = z.object({
  metric: z.enum(['organic_traffic', 'rankings', 'leads', 'conversions', 'visibility']),
  baseline_window_days: z.number().int().min(30).max(365).default(90),
  target_delta_pct: z.number().min(0).max(500),
  target_scope: z.string(),
  time_horizon_days: z.number().int().min(7).max(365),
  rationale: z.string(),
});
export type GoalParseOutput = z.infer<typeof GoalParseOutputSchema>;

export const StrategyDecomposeOutputSchema = z.object({
  archetype: StrategyArchetypeSchema,
  rationale: z.string(),
  key_assumptions: z.array(z.string()).min(1).max(6),
  steps: z
    .array(
      z.object({
        step_type: CampaignStepTypeSchema,
        title: z.string().min(3).max(200),
        rationale: z.string().min(3).max(500),
        depends_on_seq: z.array(z.number().int().min(0)).default([]),
        payload: z.record(z.unknown()).default({}),
      }),
    )
    .min(1)
    .max(12),
});
export type StrategyDecomposeOutput = z.infer<typeof StrategyDecomposeOutputSchema>;

export const ReplanOutputSchema = z.object({
  rationale: z.string(),
  add_steps: z
    .array(
      z.object({
        step_type: CampaignStepTypeSchema,
        title: z.string().min(3).max(200),
        rationale: z.string().min(3).max(500),
        depends_on_seq: z.array(z.number().int().min(0)).default([]),
        payload: z.record(z.unknown()).default({}),
      }),
    )
    .max(8)
    .default([]),
  skip_step_seqs: z.array(z.number().int().min(0)).default([]),
});
export type ReplanOutput = z.infer<typeof ReplanOutputSchema>;

export const CampaignBriefingOutputSchema = z.object({
  summary: z.string().min(20),
  priorities: z.array(z.string()).min(1).max(8),
  open_questions: z.array(z.string()).max(6).default([]),
});
export type CampaignBriefingOutput = z.infer<typeof CampaignBriefingOutputSchema>;
